// Vertalingen van het beheer. Catalogi per gebied in src/i18n/<taal>/<gebied>.json, samengevoegd per taal.
// ICU-berichten (meervoud, keuze) via intl-messageformat. Nederlands is de brontaal; Engels is compleet.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { IntlMessageFormat } from "intl-messageformat";

export type UiLocale = "nl" | "en";
export const UI_LOCALES: { id: UiLocale; label: string; tag: string }[] = [
  { id: "nl", label: "Nederlands", tag: "nl-NL" },
  { id: "en", label: "English", tag: "en-GB" },
];

const files = import.meta.glob<Record<string, string>>("../i18n/*/*.json", { eager: true, import: "default" });
const catalogs: Record<UiLocale, Record<string, string>> = { nl: {}, en: {} };
for (const [path, messages] of Object.entries(files)) {
  const locale = /\/i18n\/(\w+)\//.exec(path)?.[1] as UiLocale | undefined;
  if (locale && catalogs[locale]) Object.assign(catalogs[locale], messages);
}

export type Translate = (key: string, values?: Record<string, unknown>) => string;

interface I18nValue {
  locale: UiLocale;
  tag: string;
  setLocale: (l: UiLocale) => void;
  t: Translate;
}

const I18nContext = createContext<I18nValue | null>(null);
const STORAGE_KEY = "bres:ui-locale";

function initialLocale(): UiLocale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "nl" || stored === "en") return stored;
  } catch {
    // geen opslag beschikbaar
  }
  return navigator.language.toLowerCase().startsWith("nl") ? "nl" : "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<UiLocale>(initialLocale);
  const tag = UI_LOCALES.find((l) => l.id === locale)!.tag;
  const cache = useMemo(() => new Map<string, IntlMessageFormat>(), [locale]);
  const t = useCallback<Translate>(
    (key, values) => {
      const source = catalogs[locale][key] ?? catalogs.nl[key];
      if (source == null) {
        if (import.meta.env.DEV) console.warn(`[i18n] ontbrekende sleutel ${key}`);
        return key;
      }
      if (!values && !source.includes("{")) return source;
      let fmt = cache.get(key);
      if (!fmt) {
        fmt = new IntlMessageFormat(source, tag);
        cache.set(key, fmt);
      }
      return String(fmt.format(values as Record<string, string>));
    },
    [locale, tag, cache],
  );
  const setLocale = useCallback((l: UiLocale) => {
    setLocaleState(l);
    document.documentElement.lang = l;
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      // geen opslag beschikbaar
    }
  }, []);
  document.documentElement.lang = locale;
  const value = useMemo(() => ({ locale, tag, setLocale, t }), [locale, tag, setLocale, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("I18nProvider ontbreekt");
  return value;
}

export function useT(): Translate {
  return useI18n().t;
}

/** Voor tests en de copycheck. */
export const __catalogs = catalogs;
